import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Search, 
  Star, 
  X,
  SlidersHorizontal,
  Clock,
  ChevronDown,
  Loader2,
  UtensilsCrossed,
  Heart,
  ShoppingBag,
  MapPin,
  ArrowLeft
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../utils/images';

const sortOptions = [
  { value: 'relevance', label: 'Pertinence' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
  { value: 'rating', label: 'Meilleure note' },
];

const priceRanges = [
  { value: 'all', label: 'Tous les prix', min: null, max: null },
  { value: 'budget', label: '€ - Moins de 10€', min: 0, max: 10 },
  { value: 'medium', label: '€€ - 10€ à 20€', min: 10, max: 20 },
  { value: 'premium', label: '€€€ - Plus de 20€', min: 20, max: null },
];

// Filter Panel Component (extracted to avoid nested component issue)
function FilterPanel({ 
  mobile = false, 
  priceRange, 
  setPriceRange, 
  minRating, 
  setMinRating, 
  openNow, 
  setOpenNow, 
  applyFilters 
}) {
  return (
    <div className={`space-y-6 ${mobile ? '' : 'p-4 bg-white rounded-2xl border border-gray-200'}`}>
      {/* Price Filter */}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">
          Gamme de prix
        </Label>
        <div className="space-y-2">
          {priceRanges.map((range) => (
            <label 
              key={range.value}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                priceRange === range.value 
                  ? 'bg-emerald-50 border-emerald-200 border' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name="price"
                value={range.value}
                checked={priceRange === range.value}
                onChange={(e) => {
                  setPriceRange(e.target.value);
                  if (!mobile) applyFilters({ priceRange: e.target.value });
                }}
                className="text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm">{range.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Rating Filter */}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">
          Note minimum du restaurant
        </Label>
        <div className="space-y-2">
          {['', '3', '4', '4.5'].map((rating) => (
            <label 
              key={rating || 'all'}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                minRating === rating 
                  ? 'bg-emerald-50 border-emerald-200 border' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name="rating"
                value={rating}
                checked={minRating === rating}
                onChange={(e) => {
                  setMinRating(e.target.value);
                  if (!mobile) applyFilters({ minRating: e.target.value });
                }}
                className="text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm flex items-center gap-1">
                {rating ? (
                  <>
                    {rating}+ <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  </>
                ) : (
                  'Toutes notes'
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Open Now Filter */}
      <div>
        <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
          openNow 
            ? 'bg-emerald-50 border-emerald-200 border' 
            : 'hover:bg-gray-50 border border-transparent'
        }`}>
          <Checkbox
            checked={openNow}
            onCheckedChange={(checked) => {
              setOpenNow(checked);
              if (!mobile) applyFilters({ openNow: checked });
            }}
          />
          <span className="text-sm font-medium">
            <Clock className="w-4 h-4 inline mr-1" />
            Restaurant ouvert
          </span>
        </label>
      </div>

      {/* Apply Button (Mobile) */}
      {mobile && (
        <SheetClose asChild>
          <Button 
            onClick={() => applyFilters()}
            className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
          >
            Appliquer les filtres
          </Button>
        </SheetClose>
      )}
    </div>
  );
}

export default function SearchDishes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Filter state
  const [priceRange, setPriceRange] = useState(searchParams.get('price') || 'all');
  const [minRating, setMinRating] = useState(searchParams.get('rating') || '');
  const [openNow, setOpenNow] = useState(searchParams.get('open') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  
  // Favorites
  const [favoriteDishes, setFavoriteDishes] = useState([]);
  
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch favorites
  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    }
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites/dishes`);
      setFavoriteDishes(response.data.map(d => d.id));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const searchDishes = useCallback(async (query, filters = {}) => {
    if (!query || query.length < 2) {
      setDishes([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    try {
      const params = new URLSearchParams();
      params.set('q', query);
      
      // Price filter
      const selectedPrice = priceRanges.find(p => p.value === (filters.priceRange || priceRange));
      if (selectedPrice && selectedPrice.min !== null) {
        params.set('min_price', selectedPrice.min);
      }
      if (selectedPrice && selectedPrice.max !== null) {
        params.set('max_price', selectedPrice.max);
      }
      
      // Rating filter
      if (filters.minRating || minRating) {
        params.set('min_rating', filters.minRating || minRating);
      }
      
      // Open now filter
      if (filters.openNow !== undefined ? filters.openNow : openNow) {
        params.set('open_now', 'true');
      }
      
      // Sort
      params.set('sort_by', filters.sortBy || sortBy);
      
      const response = await axios.get(`${API}/restaurants/search/dishes?${params.toString()}`);
      setDishes(response.data);
    } catch (error) {
      console.error('Error searching dishes:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  }, [priceRange, minRating, openNow, sortBy]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchDishes(searchQuery);
      }, 300);
    } else {
      setDishes([]);
      setHasSearched(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, searchDishes]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (priceRange !== 'all') params.set('price', priceRange);
    if (minRating) params.set('rating', minRating);
    if (openNow) params.set('open', 'true');
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    setSearchParams(params, { replace: true });
  }, [searchQuery, priceRange, minRating, openNow, sortBy, setSearchParams]);

  const handleSearch = (e) => {
    e?.preventDefault();
    searchDishes(searchQuery);
  };

  const applyFilters = (newFilters = {}) => {
    const filters = {
      priceRange: newFilters.priceRange ?? priceRange,
      minRating: newFilters.minRating ?? minRating,
      openNow: newFilters.openNow ?? openNow,
      sortBy: newFilters.sortBy ?? sortBy,
    };
    searchDishes(searchQuery, filters);
  };

  const clearFilters = () => {
    setPriceRange('all');
    setMinRating('');
    setOpenNow(false);
    setSortBy('relevance');
    searchDishes(searchQuery, { priceRange: 'all', minRating: '', openNow: false, sortBy: 'relevance' });
  };

  const toggleFavorite = async (dishId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      const isFavorite = favoriteDishes.includes(dishId);
      
      if (isFavorite) {
        await axios.delete(`${API}/favorites/dishes/${dishId}`);
        setFavoriteDishes(prev => prev.filter(id => id !== dishId));
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/dishes/${dishId}`, {});
        setFavoriteDishes(prev => [...prev, dishId]);
        toast.success('Ajouté aux favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des favoris');
    }
  };

  const activeFiltersCount = [
    priceRange !== 'all',
    minRating,
    openNow,
  ].filter(Boolean).length;

  return (
    <Layout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            {/* Back Link & Title */}
            <div className="flex items-center gap-3 mb-4">
              <Link 
                to="/restaurants" 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Rechercher un plat</h1>
                <p className="text-sm text-gray-500">Trouvez votre plat préféré directement</p>
              </div>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Burger, pizza, sushi, salade..."
                  className="pl-10 pr-10 h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setDishes([]);
                      setHasSearched(false);
                      inputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
              >
                <Search className="w-5 h-5" />
              </Button>
            </form>

            {/* Quick Filters */}
            {hasSearched && (
              <div className="flex items-center gap-2 flex-wrap mt-4">
                {/* Sort Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full gap-1">
                      {sortOptions.find(s => s.value === sortBy)?.label}
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {sortOptions.map((option) => (
                      <DropdownMenuItem 
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          applyFilters({ sortBy: option.value });
                        }}
                        className={sortBy === option.value ? 'bg-emerald-50' : ''}
                      >
                        {option.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Quick Filter Buttons */}
                <Button 
                  variant={openNow ? "default" : "outline"} 
                  size="sm" 
                  className={`rounded-full ${openNow ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                  onClick={() => {
                    setOpenNow(!openNow);
                    applyFilters({ openNow: !openNow });
                  }}
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Ouvert
                </Button>

                <Button 
                  variant={minRating === '4' ? "default" : "outline"} 
                  size="sm" 
                  className={`rounded-full ${minRating === '4' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                  onClick={() => {
                    const newRating = minRating === '4' ? '' : '4';
                    setMinRating(newRating);
                    applyFilters({ minRating: newRating });
                  }}
                >
                  <Star className="w-4 h-4 mr-1 fill-yellow-400 text-yellow-400" />
                  4+ étoiles
                </Button>

                {/* Mobile Filter Button */}
                <div className="lg:hidden">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-full relative">
                        <SlidersHorizontal className="w-4 h-4 mr-1" />
                        Filtres
                        {activeFiltersCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center">
                            {activeFiltersCount}
                          </span>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-full sm:w-96 overflow-y-auto">
                      <SheetHeader>
                        <SheetTitle className="flex items-center justify-between">
                          Filtres
                          {activeFiltersCount > 0 && (
                            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500">
                              Effacer tout
                            </Button>
                          )}
                        </SheetTitle>
                      </SheetHeader>
                      <div className="py-4">
                        <FilterPanel 
                          mobile 
                          priceRange={priceRange}
                          setPriceRange={setPriceRange}
                          minRating={minRating}
                          setMinRating={setMinRating}
                          openNow={openNow}
                          setOpenNow={setOpenNow}
                          applyFilters={applyFilters}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>

                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-1 rounded-full text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Effacer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-8">
            {/* Desktop Sidebar Filters */}
            {hasSearched && (
              <aside className="hidden lg:block w-72 shrink-0">
                <div className="sticky top-40">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-gray-900">Filtres</h2>
                    {activeFiltersCount > 0 && (
                      <button onClick={clearFilters} className="text-sm text-red-500 hover:underline">
                        Effacer tout
                      </button>
                    )}
                  </div>
                  <FilterPanel 
                    priceRange={priceRange}
                    setPriceRange={setPriceRange}
                    minRating={minRating}
                    setMinRating={setMinRating}
                    openNow={openNow}
                    setOpenNow={setOpenNow}
                    applyFilters={applyFilters}
                  />
                </div>
              </aside>
            )}

            {/* Results */}
            <div className="flex-1">
              {/* Initial State */}
              {!hasSearched && !loading && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <UtensilsCrossed className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Recherchez un plat</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    Tapez le nom d&apos;un plat pour le trouver parmi tous les restaurants disponibles
                  </p>
                  
                  {/* Popular Searches */}
                  <div className="mt-8">
                    <p className="text-sm text-gray-500 mb-3">Recherches populaires</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {['Burger', 'Pizza', 'Sushi', 'Salade', 'Poke Bowl', 'Tacos', 'Pasta'].map((item) => (
                        <button
                          key={item}
                          onClick={() => setSearchQuery(item)}
                          className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-emerald-300 hover:text-emerald-600 transition-colors"
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  <span className="ml-3 text-gray-600">Recherche en cours...</span>
                </div>
              )}

              {/* Results */}
              {!loading && hasSearched && (
                <>
                  <p className="text-gray-600 mb-6">
                    {dishes.length} plat{dishes.length !== 1 ? 's' : ''} trouvé{dishes.length !== 1 ? 's' : ''} pour &quot;{searchQuery}&quot;
                  </p>

                  {dishes.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                        <Search className="w-10 h-10 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun plat trouvé</h3>
                      <p className="text-gray-500 mb-6">Essayez de modifier votre recherche ou vos filtres</p>
                      <Button onClick={clearFilters} variant="outline" className="rounded-full">
                        Effacer les filtres
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {dishes.map((dish) => (
                        <Link
                          key={dish.id}
                          to={`/restaurants/${dish.restaurant?.id}`}
                          className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-emerald-200 transition-all"
                        >
                          {/* Dish Image */}
                          <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                            {dish.image ? (
                              <img 
                                src={getFullImageUrl(dish.image)} 
                                alt={dish.name}
                                loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <UtensilsCrossed className="w-12 h-12 text-gray-300" />
                              </div>
                            )}
                            
                            {/* Favorite Button */}
                            <button
                              onClick={(e) => toggleFavorite(dish.id, e)}
                              className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                favoriteDishes.includes(dish.id)
                                  ? 'bg-red-500 text-white'
                                  : 'bg-white/90 text-gray-600 hover:text-red-500'
                              }`}
                            >
                              <Heart className={`w-5 h-5 ${favoriteDishes.includes(dish.id) ? 'fill-current' : ''}`} />
                            </button>

                            {/* Price Badge */}
                            <div className="absolute bottom-3 left-3">
                              <Badge className="bg-white text-emerald-600 font-bold px-3 py-1 shadow-sm">
                                {(Number(dish.price) || 0).toFixed(2)} €
                              </Badge>
                            </div>

                            {/* Unavailable Overlay */}
                            {!dish.is_available && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <Badge className="bg-red-500 text-white">Épuisé</Badge>
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-4">
                            <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-1">
                              {dish.name}
                            </h3>
                            
                            {dish.description && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {dish.description}
                              </p>
                            )}

                            {/* Restaurant Info */}
                            {dish.restaurant && (
                              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                                <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                                  {dish.restaurant.cover_image ? (
                                    <img 
                                      src={getFullImageUrl(dish.restaurant.cover_image)} 
                                      alt={dish.restaurant.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <ShoppingBag className="w-4 h-4 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-700 truncate">
                                    {dish.restaurant.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    {dish.restaurant.avg_rating > 0 && (
                                      <span className="flex items-center gap-0.5">
                                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                        {dish.restaurant.avg_rating.toFixed(1)}
                                      </span>
                                    )}
                                    {dish.restaurant.city && (
                                      <span className="flex items-center gap-0.5">
                                        <MapPin className="w-3 h-3" />
                                        {dish.restaurant.city}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Open Status */}
                                <Badge 
                                  variant="outline" 
                                  className={`shrink-0 text-xs ${
                                    dish.restaurant.is_currently_open
                                      ? 'text-emerald-600 border-emerald-200 bg-emerald-50'
                                      : 'text-red-600 border-red-200 bg-red-50'
                                  }`}
                                >
                                  {dish.restaurant.is_currently_open ? 'Ouvert' : 'Fermé'}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
